/* Las frases del detector externo (Pangram) y de «dar la novela por cerrada». Lógica pura: no importa nada, para
   poder probarla sin Word ni navegador. Lo que se dice aquí es lo que el autor lee ANTES de que su texto salga
   del ordenador, así que cada frase tiene su prueba. */

export type Zona = "verde" | "ambar" | "rojo";
export type ResumenDePangram = {
  palabras: number; pct_ia: number; pct_asistida: number; pct_humano: number; pct_no_humano: number; zona: Zona | null; aviso: boolean;
  bloques: number; humanizadas: number; modelo: string | null; version: string | null;
  capitulos: { n: number; titulo: string; palabras: number; pct_no_humano: number; zona: Zona | null; aviso: boolean }[];
};
export type EstadoDelDetector = {
  hay: boolean; motivo?: "sin_documento" | "sin_guion" | "sin_novela" | "varios_libros";
  fichero?: string | null;
  /** Solo cuando se mira la novela del libro: cuál es y cuántas escenas lleva. */
  novela?: { fichero: string; existe: boolean; escenas: number; total: number } | null;
  /** La novela dada por cerrada por el autor (o `null`). `al_dia`: lo cerrado sigue siendo la novela de ahora. */
  cierre?: { cuando: string; fichero: string; escenas: number; total: number; al_dia: boolean; fichero_de_ahora: string | null } | null;
  /** Cambios sin aceptar ni rechazar en la novela (solo se mira antes de cerrarla). */
  marcas?: number | null;
  llave: boolean; hay_motor: boolean; servicio: string;
  tarifa: { modelo: string; usd_por_100_palabras: number; mirada: string };
  /** Lo ya medido de ESE fichero: entero o a medias. */
  medida: { fichero: string; empezada: string; terminada: string | null; bloques: number; hechos: number; entera: boolean; resumen: ResumenDePangram | null; omitidos?: number; palabras_omitidas?: number; no_medidos?: number; palabras_no_medidas?: number } | null;
  /** Lo que viajaría ahora: solo se calcula cuando se puede ofrecer. */
  viaja: { bloques: number; palabras: number; hechos: number; faltan: number; palabras_que_faltan: number; coste: number; coste_que_falta: number; omitidos?: number; palabras_omitidas?: number; no_medidos?: number; palabras_no_medidas?: number } | null;
  /**
   * COMPRAR EL PASO (quien no tiene llave en su ordenador, que son todos menos Rais): lo que costaría lo que falta.
   * `disponible`: `true` se puede comprar · `false` el servidor dice que todavía no · `null` no se ha podido preguntar.
   * `pagado`: este fichero ya tiene un pedido pagado y no se cobra nada más. `pendiente`: hay una página de pago abierta.
   */
  venta?: { disponible: boolean | null; pagado: boolean; pendiente: boolean; importe_cent: number | null; moneda?: string; cent_por_1000?: number; minimo_cent?: number;
    /** Lo ya pagado no llega a todo lo que falta: cubre `quedan` palabras; se mide hasta ahí y el resto se compra después. */
    parcial?: boolean; quedan?: number } | null;
  /** El pedido de este fichero, si lo hay. `url`: su página de pago, mientras está por pagar. */
  pedido?: { estado: "pendiente" | "pagado"; importe_cent: number; url?: string | null } | null;
  /** `progreso.fase === "pago"`: se está esperando el pago y todavía no ha salido nada. `dejado`: el autor dejó de esperarlo. */
  trabajo: { id: string; estado: "en_curso" | "hecho" | "fallido"; progreso: { hecho: number; total: number; fase?: string } | null; error: string | null; dejado?: boolean } | null;
};

export const miles = (n: number): string => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
export const porCiento = (n: number): string => `${String(Math.round(n * 10) / 10).replace(".", ",")} %`;
/** «0,30 $»: dólares, que es en lo que cobra Pangram. */
export const dolares = (n: number): string => `${(Math.round(n * 100) / 100).toFixed(2).replace(".", ",")} $`;
/** «48 €», «7,50 €»: lo que se cobra al comprar el paso, que va en euros y en céntimos. */
export const euros = (cent: number): string => { const e = Math.round(cent) / 100; return `${Number.isInteger(e) ? miles(e) : e.toFixed(2).replace(".", ",")} €`; };
const tantos = (n: number, uno: string, varios: string) => `${miles(n)} ${n === 1 ? uno : varios}`;

/**
 * Lo que pasa si el autor corrige y vuelve a pasar el texto, dicho ANTES de pagar: no se paga el libro entero otra
 * vez. Se promete por CAPÍTULOS, que es lo que se cumple: un capítulo que no se toca ya está medido; uno tocado
 * puede volver entero (una palabra de más al principio mueve los cortes de sus bloques).
 */
export const SI_CAMBIAS_EL_TEXTO = "Si después cambias el texto y lo vuelves a pasar, solo viajan y se cobran los capítulos que hayas tocado.";
/** Cuando al pulsar «pagar» resulta que ya estaba pagado (se pagó después de dejar de esperar): se dice, porque el autor esperaba una página de pago. */
export const FRASE_DE_YA_PAGADO = "Ya estaba pagado: no se ha cobrado nada. El envío ha arrancado.";

/** Se está esperando el pago: todavía no ha salido nada de este ordenador. */
export const esperandoElPago = (t: EstadoDelDetector["trabajo"] | undefined): boolean => t?.estado === "en_curso" && t.progreso?.fase === "pago";
/** Hay con qué enviar: la llave de este ordenador, o un paso que se puede comprar (o ya está pagado). */
export const sePuedeEnviar = (e: EstadoDelDetector): boolean => e.llave || e.venta?.disponible === true;

export const AVISO = 5;
/** La letra pequeña que va siempre con la cifra de Pangram. */
export const LETRA_PEQUENA_DE_PANGRAM = "Es la cifra de Pangram, un detector externo: dice a qué le suena el texto y puede equivocarse, también con texto escrito sin IA. No es lo mismo que «tuyo según el registro», que cuenta lo que de verdad entró de la IA.";

/** Por qué no hay nada que ofrecer. */
export function porQueNo(e: EstadoDelDetector | null): string {
  if (!e) return "";
  if (!e.hay_motor) return "Falta el motor de documentos de HumanInk en este ordenador: reinstala HumanInk.";
  switch (e.motivo) {
    case "sin_documento": return "Guarda el documento en este ordenador (un .docx): se mide el texto guardado.";
    case "sin_guion": return "Este libro todavía no tiene guion.";
    case "varios_libros": return "En la carpeta de arriba hay más de un libro y no sé de cuál es esta novela.";
    default: return "";
  }
}

/**
 * LO QUE SE DICE ANTES DE ENVIAR NADA: qué texto sale, hacia quién, con qué llave y cuánto cuesta. Si una parte
 * ya está medida, solo viaja y se cobra lo que falta.
 */
export function fraseDeLoQueViaja(e: EstadoDelDetector): string {
  const v = e.viaja;
  if (!v) return "";
  if (!e.llave) {
    // COMPRANDO EL PASO: qué sale, por dónde pasa, lo que cuesta EN EUROS y cómo se paga. Sin llave ni cuenta de nadie.
    const s = e.venta;
    if (!s?.disponible) return "";
    const aMedias = v.hechos > 0 && v.faltan > 0;
    const queSale = aMedias
      ? `Ya hay ${tantos(v.hechos, "bloque medido", "bloques medidos")} de ${miles(v.bloques)}. Van a salir de este ordenador las ${miles(v.palabras_que_faltan)} palabras que faltan de «${e.fichero}», en ${tantos(v.faltan, "bloque", "bloques")}, hacia Pangram.`
      : `Van a salir de este ordenador ${tantos(v.palabras, "palabra", "palabras")} de «${e.fichero}», en ${tantos(v.bloques, "bloque", "bloques")}, hacia Pangram.`;
    const porDonde = "Pasan por el servidor de HumanInk, que pone su llave de Pangram y no guarda el texto.";
    // Lo pagado no llega a todo: se dice hasta dónde llega y que el resto habrá que comprarlo. No se promete «nada más».
    if (s.pagado && s.parcial) return `${queSale} ${porDonde} Lo que ya tienes pagado en este libro cubre ${tantos(s.quedan ?? 0, "palabra", "palabras")} de las ${miles(v.palabras_que_faltan)} que faltan: se mide hasta donde llega, sin cobrar nada ahora, y el resto se podrá comprar después.`;
    if (s.pagado) return `${queSale} ${porDonde} Ya está pagado: no se cobra nada más.`;
    const tarifa = s.cent_por_1000 ? ` (${euros(s.cent_por_1000)} por cada 1.000 palabras${s.minimo_cent ? `, con un mínimo de ${euros(s.minimo_cent)}` : ""})` : "";
    return `${queSale} ${porDonde} Cuesta ${euros(s.importe_cent ?? 0)}${tarifa}: un pago único con tarjeta, en la página de Stripe que se abrirá en tu navegador. ${s.pendiente ? "Si ya lo has pagado, no se cobra dos veces. " : ""}En cuanto llegue el pago, el envío arranca solo. ${SI_CAMBIAS_EL_TEXTO}`;
  }
  const precio = `su tarifa es de ${dolares(e.tarifa.usd_por_100_palabras)} por cada 100 palabras`;
  if (v.hechos > 0 && v.faltan > 0) {
    return `Ya hay ${tantos(v.hechos, "bloque medido", "bloques medidos")} de ${miles(v.bloques)}, y no se vuelven a cobrar. Van a salir de este ordenador las ${miles(v.palabras_que_faltan)} palabras que faltan de «${e.fichero}», en ${tantos(v.faltan, "bloque", "bloques")}, hacia Pangram, con tu llave. Costará unos ${dolares(v.coste_que_falta)} de tu cuenta de Pangram (${precio}).`;
  }
  return `Van a salir de este ordenador ${tantos(v.palabras, "palabra", "palabras")} de «${e.fichero}», en ${tantos(v.bloques, "bloque", "bloques")}, hacia Pangram, con tu llave. Costará unos ${dolares(v.coste)} de tu cuenta de Pangram (${precio}).`;
}

/** Lo que NO se mide (y no se cobra): trozos demasiado cortos, o que Pangram no pudo medir. No están en la cifra. */
export function fraseDeOmitidos(o: { omitidos?: number; palabras_omitidas?: number } | null | undefined): string {
  const n = o?.omitidos ?? 0;
  if (!n) return "";
  return `${n === 1 ? "Un trozo no se mide" : `${miles(n)} trozos no se miden`} (${tantos(o?.palabras_omitidas ?? 0, "palabra", "palabras")}): ${n === 1 ? "es demasiado corto" : "son demasiado cortos"} para que un detector diga algo fiable. No se ${n === 1 ? "envía ni se cobra" : "envían ni se cobran"}, y no ${n === 1 ? "está" : "están"} en la cifra.`;
}

/**
 * Lo que Pangram NO PUDO MEDIR: se envió y no dio cifra. No es lo mismo que «demasiado corto» ni se dice igual (a quien
 * compró el paso se le decía «no se envía ni se cobra» de un trozo que sí se había enviado). `comprado`: el texto pasó
 * por el servidor con un pedido pagado, y sus palabras se han devuelto a lo pagado.
 */
export function fraseDeNoMedidos(o: { no_medidos?: number; palabras_no_medidas?: number } | null | undefined, comprado: boolean): string {
  const n = o?.no_medidos ?? 0;
  if (!n) return "";
  return `Pangram no ha podido medir ${n === 1 ? "un trozo" : `${miles(n)} trozos`} (${tantos(o?.palabras_no_medidas ?? 0, "palabra", "palabras")}): no ${n === 1 ? "está" : "están"} en la cifra${comprado ? ", y sus palabras se han devuelto a lo que tienes pagado" : ""}.`;
}

/** El rótulo del primer botón: lo que cuesta, a la vista antes de pulsar. */
export function rotuloDePasar(e: EstadoDelDetector): string {
  const v = e.viaja;
  if (!v) return "Pasar por Pangram";
  if (!e.llave) {
    const s = e.venta, verbo = v.hechos > 0 && v.faltan > 0 ? "Seguir con Pangram" : "Pasar por Pangram";
    if (!s?.disponible) return verbo;
    return s.pagado ? `${verbo} · ${s.parcial ? "pagado en parte" : "ya pagado"}` : `${verbo} · ${euros(s.importe_cent ?? 0)}`;
  }
  return v.hechos > 0 && v.faltan > 0 ? `Seguir con Pangram · unos ${dolares(v.coste_que_falta)}` : `Pasar por Pangram · unos ${dolares(v.coste)}`;
}

/** El rótulo del SEGUNDO clic: si va a cobrar, lo dice con su cifra. */
export function rotuloDeConfirmar(e: EstadoDelDetector): string {
  const s = e.venta;
  return !e.llave && s?.disponible && !s.pagado ? `Pagar ${euros(s.importe_cent ?? 0)} y enviar el texto` : "Sí, enviar el texto a Pangram";
}

/** Por qué no se puede comprar el paso ("" si se puede, o si no hay nada que ofrecer). */
export function fraseDeNoSeVende(e: EstadoDelDetector): string {
  if (e.llave || !e.venta || e.venta.disponible === true) return "";
  return e.venta.disponible === false
    ? "Pasar un texto por Pangram es un servicio aparte de HumanInk, de pago único por manuscrito. Todavía no se puede contratar desde aquí."
    : "No consigo hablar con el servidor de HumanInk para darte el precio. Comprueba la conexión y vuelve a esta pestaña.";
}

/** Lo que se dice cuando el autor deja de esperar el pago: no es un fallo, y si llegó a pagar no lo pierde. */
export const FRASE_DE_DEJADO = "Has dejado de esperar el pago y no ha salido nada de este ordenador. Si llegaste a pagar, pulsa otra vez: no se cobra dos veces.";

/** «93,4 % humano · 4,1 % asistido por IA · 2,5 % IA». */
export const fraseDeResultado = (r: ResumenDePangram): string => `${porCiento(r.pct_humano)} humano · ${porCiento(r.pct_asistida)} asistido por IA · ${porCiento(r.pct_ia)} IA`;

/** El aviso del 5 %, con la cifra de Pangram: lo que no da por humano. */
export const fraseDelAviso = (r: ResumenDePangram): string => (r.aviso ? `Pasa del ${AVISO} %: Pangram no da por humano el ${porCiento(r.pct_no_humano)} del texto.` : "");

/** Los capítulos que pasan del aviso, del peor al mejor: dónde mirar. */
export const capitulosConAviso = (r: ResumenDePangram): { titulo: string; frase: string; zona: Zona | null }[] =>
  [...(r.capitulos ?? [])].filter((c) => c.aviso).sort((a, b) => b.pct_no_humano - a.pct_no_humano)
    .map((c) => ({ titulo: c.titulo || `Capítulo ${c.n}`, frase: `${porCiento(c.pct_no_humano)} no humano, en ${miles(c.palabras)} palabras`, zona: c.zona }));

/** Pangram marca aparte lo que le parece texto de IA retocado para parecer humano. */
export const fraseDeHumanizadas = (r: ResumenDePangram): string =>
  (r.humanizadas > 0 ? `Pangram marca ${tantos(r.humanizadas, "fragmento", "fragmentos")} como «humanizado»: texto de IA retocado para parecer humano.` : "");

/** «Midiendo… bloque 12 de 32». */
export function fraseDeAvance(t: EstadoDelDetector["trabajo"]): string {
  if (!t || t.estado !== "en_curso") return "";
  if (esperandoElPago(t)) return "Esperando el pago en tu navegador. En cuanto llegue, el envío arranca solo; hasta entonces no sale nada de este ordenador.";
  const p = t.progreso;
  return p && p.total ? `Midiendo con Pangram… ${p.hecho >= p.total ? "terminando" : `bloque ${miles(p.hecho + 1)} de ${miles(p.total)}`}. Puedes seguir trabajando.` : "Preparando el envío a Pangram…";
}

/** Lo que se le dice al autor antes de dar la novela por cerrada: qué fichero es y cuántas escenas lleva. */
export function fraseDeCerrar(e: EstadoDelDetector): string {
  const n = e.novela;
  if (!n) return "";
  const faltan = n.total - n.escenas;
  return `Se cierra «${n.fichero}», con ${miles(n.escenas)} de ${tantos(n.total, "escena", "escenas")} del guion.${faltan > 0 ? ` ${faltan === 1 ? "Queda una escena" : `Quedan ${miles(faltan)} escenas`} sin cerrar: si es así como la quieres, adelante.` : ""} Cerrarla no gasta nada y se puede reabrir.`;
}

/** «10 oct, 22:15», en la hora del autor; "" si no hay fecha. */
export function diaYHora(iso: string | null | undefined): string {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
  return `${d.getDate()}-${MESES[d.getMonth()]}, ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}
